"use client"

import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// Sources:
//   • EPA Section 608 of the Clean Air Act: regulates the handling, sale,
//     recovery, and disposal of refrigerants. Service involving refrigerant
//     must be performed by a Section 608-certified technician.
//     https://www.epa.gov/section608
//   • EPA Significant New Alternatives Policy (SNAP): lists acceptable
//     refrigerants for household refrigerators. Most modern domestic
//     refrigerators use R-600a (isobutane) — flammable, classified A3.
//   • EPA SNAP Rule 20 / 21: charge limit for R-600a in household
//     refrigerators is 150 g (5.29 oz).
//   • Manufacturer service manuals (Whirlpool, GE, LG, Samsung): refrigerant
//     leak diagnosis (electronic leak detector, ultraviolet dye, soap
//     bubbles); only Section 608-certified technicians may add or recover
//     refrigerant.
//   • USDA FSIS — refrigerator must hold ≤ 40°F; food held above 40°F for
//     more than 2 hours should be discarded.

const SECTIONS = [
  { id: "what", t: "What it is" },
  { id: "signs", t: "Signs of a leak" },
  { id: "law", t: "What the law says" },
  { id: "diagnose", t: "How pros diagnose it" },
  { id: "decide", t: "Repair or replace" },
] as const

export function RefrigerantLeakDetectionPageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Refrigerator Refrigerant Leak Guide"
      heroEyebrow="Repair"
      heroTitlePre="When the fridge runs but"
      heroTitleItalic="won't get cold."
      heroTitlePost=""
      heroSubtitle="A sealed-system refrigerant leak presents in a specific way: the compressor runs continuously, the back of the unit is warm, but the inside drifts above 40°F. Here's how pros diagnose it, what the law requires, and when the repair stops being worth it."
      heroMeta={["⏱ ~6 min read", "EPA Section 608 + SNAP"]}
      heroShare={<ToolShareBar title="Refrigerator Refrigerant Leak Guide" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            Federal rule
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            EPA Section 608 requires that anyone servicing refrigerant be a{" "}
            <em className="italic text-[#ff9951]">certified technician</em>. DIY refrigerant work
            is not legal.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Food-safety line
              </div>
              <div className="text-[22px] font-black tracking-tight">40°F</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Discard after
              </div>
              <div className="text-[22px] font-black tracking-tight">2 hr</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Can I just recharge the refrigerant myself?",
          answer:
            "No. EPA Section 608 of the Clean Air Act requires that anyone who handles refrigerant be EPA-certified. Sale of small refrigerant cans for unlicensed use is restricted. Modern domestic refrigerators also use R-600a (isobutane) — a flammable A3 refrigerant — which compounds the safety risk of unqualified service.",
        },
        {
          question: "What refrigerant does my fridge use?",
          answer:
            "Most refrigerators built in the last 10–15 years use R-600a (isobutane) under EPA's SNAP program. Older units may use R-134a, and units made before the late 1990s may use R-12 (a CFC, banned for new equipment under the Montreal Protocol). The model nameplate lists the refrigerant and charge weight in grams.",
        },
        {
          question: "How do I tell a refrigerant leak from a different problem?",
          answer:
            "Three classic symptoms together strongly suggest a sealed-system leak: (1) compressor runs continuously, (2) condenser coils are warm/hot to the touch, but (3) the interior won't reach setpoint. Frost patterns isolated to the start of the evaporator and an oily residue near brazed joints support the diagnosis. Always rule out the easier causes first — dirty coils, failed condenser fan, failed evaporator fan, blocked airflow.",
        },
        {
          question: "Is a leaking fridge safe to leave running?",
          answer:
            "If you smell anything chemical or notice an oily residue on the floor, unplug the unit and ventilate the kitchen. R-600a is flammable; even a small leaking charge can reach a flammable concentration in a closed cabinet. EPA SNAP caps R-600a charge in household refrigerators at 150 g for this reason.",
        },
        {
          question: "How much does a refrigerant leak repair cost?",
          answer:
            "Sealed-system repair is among the most expensive fridge repairs — locating the leak, brazing the line, evacuating, recharging, and verifying often runs $500–$1,000 or more. For a unit past 10 years, replacement is usually the better investment. Run the repair-vs-replace tool with your repair quote.",
        },
      ]}
      relatedTools={[
        { title: "Repair or Replace", category: "Specialty", description: "Run the math against your quote.", href: "/tools/repair-or-replace-refrigerator", time: "~1 min" },
        { title: "Refrigerator Lifespan", category: "Specialty", description: "Years left on the unit.", href: "/tools/refrigerator-lifespan-calculator", time: "~45 sec" },
        { title: "Power Outage Food Safety", category: "Food Safety", description: "Same 40°F / 2-hour rule applies.", href: "/tools/power-outage-food-safety-guide", time: "~5 min" },
        { title: "Maintenance Schedule", category: "Maintenance", description: "Catch problems before sealed-system failure.", href: "/tools/refrigerator-maintenance-schedule", time: "~5 min" },
      ]}
      cta={{
        title: "Sealed-system repair quote north of $500?",
        body: "Past 10 years, replacement almost always wins. Browse bottom-freezer refrigerators for a fresh start with a manufacturer warranty.",
        buttonLabel: "Browse bottom-freezer refrigerators",
        buttonHref: "/collections/bottom-freezer-refrigerators",
      }}
    >
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          <article>
            <GuideTool
              eyebrow="Interactive symptom check"
              title="Is it really a refrigerant leak?"
              intro="Most \u201cleak\u201d calls turn out to be coils, fans, or gaskets. Three questions to sort the likelihood before you pay for a truck roll."
              questions={[
                            {
                              id: "cooling",
                              label: "How is it cooling?",
                              options: [
                                { value: "fine", label: "Both compartments fine" },
                                { value: "one", label: "One zone warm" },
                                { value: "both", label: "Both warm" }
                              ],
                            },
                            {
                              id: "signs",
                              label: "Any of these physical signs?",
                              options: [
                                { value: "oily", label: "Oily residue near coils/lines" },
                                { value: "hiss", label: "Hissing after the compressor stops" },
                                { value: "frost", label: "Frost lump on one coil spot" },
                                { value: "none", label: "None of these" }
                              ],
                            },
                            {
                              id: "runtime",
                              label: "Compressor runtime?",
                              options: [
                                { value: "normal", label: "Cycles normally" },
                                { value: "constant", label: "Runs nearly constantly" }
                              ],
                            }
              ]}
              compute={(a) => {
                            if (a.cooling !== "fine" && (a.signs === "oily" || a.signs === "hiss" || a.signs === "frost")) {
                              return { tone: "bad", verdict: "Leak is likely \u2014 call an EPA-certified tech", detail: "Warm compartments plus oil residue, hissing, or a single frost lump are the classic leak trio. Refrigerant work is not a DIY job \u2014 handling requires EPA Section 608 certification \u2014 and on most household units a sealed-system repair competes with replacement cost." , action: { label: "Run repair-vs-replace on the quote", href: "/tools/refrigerator-repair-vs-replace-calculator" } }
                            }
                            if (a.cooling !== "fine" || a.runtime === "constant") {
                              return { tone: "warn", verdict: "Rule out the cheap causes first", detail: "Warm zones with no physical leak signs usually trace to dusty condenser coils, a dead evaporator fan, a blocked vent, or a failed door gasket \u2014 all cheap fixes. Clean the coils, verify the fan spins, run the dollar-bill gasket test, then reassess." }
                            }
                            return { tone: "good", verdict: "No leak indicators", detail: "Normal cooling and runtime with none of the physical signs \u2014 nothing here points at refrigerant. Keep the coils on a 6-month cleaning cycle and revisit if a compartment warms." }
                          }}
              ctaLabel="Check the likelihood"
              footnote="Sign set and the EPA Section 608 rule are detailed in the guide below."
            />
            <section id="what" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                What a refrigerant leak actually is
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                A refrigerator&apos;s sealed system is a closed loop: compressor, condenser,
                capillary tube, evaporator, all charged with a precise weight of refrigerant. The
                refrigerant doesn&apos;t get used up — it cycles. A leak means the loop has broken
                open somewhere, usually a brazed joint, a corroded coil, or a stress-fractured
                aluminum line.
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Once charge falls below spec, the system can&apos;t move heat efficiently. The
                compressor runs longer (then continuously), the condenser stays hot, and the
                interior drifts up. Eventually the unit can&apos;t hold USDA&apos;s 40°F line at
                all.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    R-600a is flammable
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    Most modern household refrigerators use R-600a (isobutane), a flammable
                    Class A3 refrigerant. EPA SNAP caps the charge at 150 g per unit. If you
                    smell anything chemical or see oily residue, unplug the unit, open windows,
                    and call a Section 608-certified tech.
                  </p>
                </div>
              </div>
            </section>

            <section id="signs" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Signs of a leak — and what they mean
              </h2>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Symptom
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Refrigerant leak signal
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Compressor runs continuously", "Trying to compensate for low charge"],
                      ["Interior won't drop below 50°F", "Insufficient cooling capacity"],
                      ["Frost only at the start of evaporator", "Charge starved at distribution"],
                      ["Condenser warm but not hot", "Reduced heat-of-rejection"],
                      ["Hissing or gurgling that wasn't there before", "Liquid/vapor flashing at leak point"],
                      ["Oily film near a brazed joint", "Refrigerant oil escaping with the leak"],
                      ["Faint chemical smell", "Refrigerant escaping into the room"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4 text-gray-700">{r[1]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[13px] text-gray-500 mt-4">
                Always rule out the easier causes first: dirty condenser coils, failed condenser
                or evaporator fan, blocked airflow, or door gasket failure. A dirty coil mimics
                most of these symptoms and is fixed in 10 minutes.
              </p>
            </section>

            <section id="law" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                What the law actually says
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Refrigerant work in the United States is governed by Section 608 of the federal
                Clean Air Act. The rules are not advisory.
              </p>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Only certified technicians may handle refrigerant", d: "EPA Section 608 requires technician certification for anyone who maintains, services, repairs, or disposes of equipment containing refrigerant. Type I covers small appliances, including domestic refrigerators." },
                  { n: "02", t: "Venting is prohibited", d: "It is illegal to knowingly vent refrigerant during service. Recovery is mandatory. Civil penalties apply." },
                  { n: "03", t: "Disposal must include refrigerant recovery", d: "When disposing of a refrigerator, refrigerant must be recovered by a certified tech before the unit goes to scrap. Many municipalities offer appliance recycling that handles this; some retailers recover refrigerant when delivering a replacement." },
                  { n: "04", t: "R-600a charge is capped at 150 g per unit", d: "EPA SNAP rules limit the per-appliance charge of R-600a in household refrigerators to 150 g (5.29 oz) precisely because it is flammable." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="diagnose" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                How a Section 608 tech actually finds it
              </h2>
              <div className="space-y-3">
                {[
                  { t: "Electronic leak detector", d: "Heated-diode or infrared sensor swept along brazed joints, return bends, and any visible oil staining. Most sensitive method for the small charges in domestic units." },
                  { t: "Ultraviolet dye + UV lamp", d: "A small dose of UV dye is added with the recharge. Future leaks fluoresce under UV inspection. Useful for slow leaks that an electronic detector misses." },
                  { t: "Soap-bubble test", d: "A pressurized line gets a thin coat of leak-detection solution. Bubbles form at the leak. Cheap, low-tech, and effective on accessible joints — usually a confirmation step after the electronic sweep." },
                  { t: "Pressure decay test", d: "After repair, the system is evacuated and then pressurized with dry nitrogen. Pressure is monitored over time; any decay indicates a remaining leak before recharge." },
                ].map((r, i) => (
                  <div
                    key={r.t}
                    className="grid grid-cols-[36px_1fr] sm:grid-cols-[44px_1fr] gap-4 sm:gap-5 p-4 sm:p-5 rounded-lg"
                    style={{ background: i === 0 ? "rgba(255,153,81,0.04)" : "#f9fafb" }}
                  >
                    <div
                      className="font-mono font-bold"
                      style={{
                        fontSize: 14,
                        letterSpacing: "0.05em",
                        color: i === 0 ? "#ff9951" : "#9ca3af",
                        lineHeight: 1.4,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="text-[18px] font-extrabold tracking-[-0.015em] mb-1 text-gray-900">
                        {r.t}
                      </h3>
                      <p className="text-[14px] text-gray-600 leading-[1.55]">{r.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="decide" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Repair-or-replace decision
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Past 10 years old — replacement usually wins",
                  "Repair quote > 50% of replacement cost — replace",
                  "Multiple systems failing (gasket + ice maker + leak) — replace",
                  "Unit is < 5 years old and under warranty — file a claim first",
                  "Slow leak located, joint is accessible — repair often viable",
                  "Evaporator coil leak (inside foam) — repair rarely viable; replace",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>
          </article>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Fresh start"
          title="Bottom-freezer refrigerators"
          description="Modern R-600a sealed systems with full manufacturer warranty."
          collectionType="bottom-freezer"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
